import type { SyntheticEvent } from "react";
import React, { useState, useRef } from "react";
import * as Flex from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Flex";
import type { UseExtrasCategoryFilterType } from "../../../../hooks/shared/extras/useExtrasCategoryFilter";
import useExtraCategoryFilter from "../../../../hooks/shared/extras/useExtrasCategoryFilter";
import { useBnbSelector } from "../../../../redux/store";
import Icon from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Icon";
import { BnbLabel, useBnbLabel } from "../../../../utils/labels";
import * as ExtraFilterStyles from "./styles/ExtraFilter";
import * as Type from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Type";
import * as Dropdown from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Dropdown";
import type { ExtrasCategoryToFilterType } from "../../../../../../../common-deprecated/types/CommonTypes";
import { useCloseOnOutsideClick } from "../../../../../../../common-deprecated/hooks";
import { CategoryIconComponent } from "./ExtrasCategoryIcons";
import { IconWrapper } from "../../../../../../../common-deprecated/styles/v2/lexus/globals/IconWrapper";
import ButtonPrimary from "../../../../../../../common-deprecated/components/aem/lexus/button/ButtonPrimary";
import { trackExtrasFirstSearch } from "../../../../utils/tracking";

type ExtraFilterPopupType = {
    filterProps: UseExtrasCategoryFilterType;
};

export const ExtraFilterPopup = (props: ExtraFilterPopupType): JSX.Element => {
    const {
        categories,
        rootCategories,
        onSetTempRootCategoryToFilter,
        onSetTempCategoriesToFilter,
        onApplyExtrasFilter,
        // TODO: this is a function to clear all active Filters, this has not yet been asked for, but chances are it will be in the distant future
        // availableOnlineToFilter,
        // onSetAvailableOnlineToFilter,
    } = props.filterProps;

    const [dropdownOpened, setDropDownOpened] = useState(false);

    const selectRootCategory = (event: SyntheticEvent, rootCategory: ExtrasCategoryToFilterType): void => {
        event.stopPropagation();
        setDropDownOpened(false);

        if (rootCategory.isSelected) return;
        if (rootCategory.id === "allExtras") {
            onSetTempRootCategoryToFilter(null);
            return;
        }
        onSetTempRootCategoryToFilter(rootCategory);
    };

    const selectCategory = (event: SyntheticEvent, selectedCategory: ExtrasCategoryToFilterType): void => {
        event.stopPropagation();
        selectedCategory.isSelected = !selectedCategory.isSelected;
        onSetTempCategoriesToFilter(categories.filter((cat) => cat.isSelected));
    };

    return (
        <ExtraFilterStyles.FilterPopover>
            {/* CategoryFilter */}
            <Type.Strong>
                <BnbLabel label="extrasFilterCategory" />
            </Type.Strong>
            {categories.map((category) => {
                return (
                    !!category.code && (
                        <ExtraFilterStyles.FilterPopoverLine
                            onClick={(event) => selectCategory(event, category)}
                            key={category.id || ""}
                        >
                            <span>
                                <CategoryIconComponent category={category.code} />
                                {`${category.name || ""} (${(category.count || "").toString()})`}
                            </span>
                            {category.isSelected && <Icon variant="close" />}
                        </ExtraFilterStyles.FilterPopoverLine>
                    )
                );
            })}

            {/* RootCategoryFilter */}
            <ExtraFilterStyles.FilterPopoverTitle mt="20px">
                <BnbLabel label="extrasFilterSearchAmong" />
            </ExtraFilterStyles.FilterPopoverTitle>
            <Dropdown.WrapperDarkVariant>
                <Dropdown.Toggle isOpen={dropdownOpened} onClick={() => setDropDownOpened(!dropdownOpened)}>
                    <span>{rootCategories.find((r) => r.isSelected)!.name}</span>
                    <Icon variant="chevronDown" />
                </Dropdown.Toggle>
                {dropdownOpened && (
                    <Dropdown.Items>
                        {rootCategories
                            .filter((r) => !r.isSelected)
                            .map((rootCat) => (
                                <Dropdown.Item key={rootCat.id || ""} onClick={(e) => selectRootCategory(e, rootCat)}>
                                    {rootCat.name}
                                </Dropdown.Item>
                            ))}
                    </Dropdown.Items>
                )}
            </Dropdown.WrapperDarkVariant>

            {/* // TODO: Wait until this is available to implement or delete from v2?
            <AvailableOnlineFilter
                availableOnlineToFilter={
                    availableOnlineToFilter
                }
                onSetAvailableOnlineToFilter={
                    onSetAvailableOnlineToFilter
                }
            />*/}

            <ButtonPrimary fullWidth onClick={onApplyExtrasFilter}>
                <BnbLabel label="extrasFilterApply" />
            </ButtonPrimary>
        </ExtraFilterStyles.FilterPopover>
    );
};

const ExtraFilter = (): JSX.Element => {
    const filterProps = useExtraCategoryFilter();
    const filterIsOpen = useBnbSelector((state) => state.extrasFilter.showExtrasFilter);
    const searchString = useBnbSelector((state) => state.extrasFilter.searchString);
    // const availableOnlineToFilter = useBnbSelector(state => state.extrasFilter.availableOnlineToFilter);

    const { categories, rootCategories, onSetSearchString, onToggleExtrasFilter } = filterProps;

    //const isFilteringAvailableOnlineToFilter = availableOnlineToFilter;
    const isFilteringOnrootFilter =
        rootCategories.filter((rootCategory) => rootCategory.isSelected)[0].id !== "allExtras";

    const numFilters = categories.filter((c) => c.isSelected).length + (isFilteringOnrootFilter ? 1 : 0); // + (isFilteringAvailableOnlineToFilter ? 1 : 0);

    const searchField = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const alreadyFiltered = useRef(false);

    const placeholderLabel = useBnbLabel("searchAllExtras", {}, "searchExtras");
    useCloseOnOutsideClick(containerRef, onToggleExtrasFilter, !filterIsOpen);

    return (
        <>
            <Flex.Row>
                <Flex.Col width={{ _: 1, md: 1 / 2, lg: 7 / 12 }} mb={4} mt={3} position="relative" ref={containerRef}>
                    <ExtraFilterStyles.FilterToggle onClick={onToggleExtrasFilter}>
                        <IconWrapper mr={1}>
                            <Icon variant="controls" />
                        </IconWrapper>
                        {numFilters > 0 && (
                            <ExtraFilterStyles.FilterBadge selected={filterIsOpen}>
                                {numFilters}
                            </ExtraFilterStyles.FilterBadge>
                        )}
                        <BnbLabel label="extrasFilterFilter" />
                    </ExtraFilterStyles.FilterToggle>

                    {filterIsOpen && <ExtraFilterPopup filterProps={filterProps} />}
                </Flex.Col>

                <Flex.Col
                    width={{ _: 1, md: 1 / 2, lg: 5 / 12 }}
                    mb={3}
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                >
                    <ExtraFilterStyles.InputGroup>
                        <IconWrapper>
                            <Icon variant="search" />
                        </IconWrapper>
                        <ExtraFilterStyles.InputGroupPrepend
                            ref={searchField}
                            type="text"
                            value={searchString || ""}
                            placeholder={placeholderLabel}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    event.preventDefault();
                                    searchField.current?.blur();
                                }
                            }}
                            onChange={(event) => {
                                onSetSearchString(event.target.value);
                                if (!alreadyFiltered.current && event.target.value) {
                                    alreadyFiltered.current = true;
                                    trackExtrasFirstSearch();
                                }
                            }}
                        />
                        {searchString && (
                            <ExtraFilterStyles.ResetButton
                                type="button"
                                onClick={(event) => {
                                    event.preventDefault();
                                    onSetSearchString("");

                                    if (searchField.current) {
                                        searchField.current.focus();
                                    }
                                }}
                            >
                                <Icon variant="close" />
                            </ExtraFilterStyles.ResetButton>
                        )}
                    </ExtraFilterStyles.InputGroup>
                </Flex.Col>
            </Flex.Row>
        </>
    );
};

export default ExtraFilter;
